import { LoadingOverlay } from "@/components/common/LoadingOverlay";
import PaginationControl from "@/components/common/PaginationControl";
import KnowledgeBaseForm from "@/components/knowledge-base/KnowledgeBaseForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { knowledgeBaseService } from "@/services/knowledgeBase.service/knowledgeBase.api";
import useStore from "@/store/useStore";
import type { KnowledgeBase as KBType } from "@/types/knowledgeBase";
import { Book, Edit, PlusCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function KnowledgeBase() {
  const navigate = useNavigate();
  const [knowledgeBases, setKnowledgeBases] = useState<KBType[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const loading = useStore(
    (state) => state.commonSlice.loaders.getAllKnowledgeBase
  );

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentKnowledgeBase, setCurrentKnowledgeBase] = useState<KBType>({
    _id: "",
    name: "",
    description: "",
    questionsCount: 0,
    isActive: true,
    channels: [],
  });

  const handleEditKnowledgeBase = async (kb: KBType) => {
    const [res, error] = await knowledgeBaseService.updateKnowledgeBase(kb._id, kb);

    if (error) {
      console.error("Erro ao atualizar base de conhecimento:", error);
      return;
    }

    if (res) {
      setKnowledgeBases(
        knowledgeBases.map((item) => (item._id === kb._id ? res : item))
      );
      setIsEditDialogOpen(false);
      toast.success("Base de conhecimento atualizada com sucesso!");
    }
  };

  const handleToggleActive = async (id: string) => {
    const kb = knowledgeBases.find((k) => k._id === id);
    if (!kb) return;

    const [res, error] = await knowledgeBaseService.updateKnowledgeBase(id, {
      isActive: !kb.isActive
    });

    if (error) {
      console.error("Erro ao atualizar status da base de conhecimento:", error);
      return;
    }

    if (res) {
      setKnowledgeBases(
        knowledgeBases.map((k) => k._id === id ? { ...k, isActive: !k.isActive } : k)
      );
      toast.success("Status da base de conhecimento atualizado!");
    }
  };

  const handleGetAllKnowledgeBases = useCallback(async () => {
    const [res, error] = await knowledgeBaseService.getAll(query, page, limit);

    if (error) {
      console.error("Erro ao buscar bases de conhecimento:", error);
      return;
    }

    if (res) {
      setKnowledgeBases(res.result);
      setTotalPages(res.qtdPages);
      setTotalItems(res.total);
    }
  }, [query, page, limit]);

  const handleCreateKnowledgeBase = async (kb: KBType) => {
    const [res, error] = await knowledgeBaseService.newKnowledgeBase(kb);

    if (error) {
      console.error("Erro ao criar base de conhecimento:", error);
      return;
    }

    if (res) {
      await handleGetAllKnowledgeBases();
      setIsCreateDialogOpen(false);
      toast.success("Base de conhecimento criada com sucesso!");
    }
  };

  const handleManageQuestions = (kb: KBType) => {
    navigate(`/knowledge-base/${kb._id}/questions`);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  useEffect(() => {
    handleGetAllKnowledgeBases();
  }, [handleGetAllKnowledgeBases]);

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Base de Conhecimento</h1>
        <Button className="gap-2" onClick={() => setIsCreateDialogOpen(true)}>
          <PlusCircle className="h-4 w-4" />
          Nova Base
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {knowledgeBases.map((kb) => (
          <Card key={kb._id} className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <Book className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-semibold">{kb.name}</h3>
              </div>
              <Badge
                variant={kb.isActive ? "default" : "secondary"}
                className="cursor-pointer"
                onClick={() => handleToggleActive(kb._id)}
              >
                {kb.isActive ? "Ativo" : "Inativo"}
              </Badge>
            </div>

            {kb.description && (
              <p className="text-sm text-muted-foreground mb-4">
                {kb.description}
              </p>
            )}

            <div className="mb-4">
              <div className="text-sm text-muted-foreground">
                {kb.questionsCount ?? 0}{" "}
                {kb.questionsCount === 1 ? "pergunta" : "perguntas"}
              </div>
              <div className="text-sm text-muted-foreground">
                {kb.channels?.length || 0}{" "}
                {(kb.channels?.length || 0) === 1
                  ? "canal vinculado"
                  : "canais vinculados"}
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleManageQuestions(kb)}
              >
                Gerenciar Perguntas
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  setCurrentKnowledgeBase(kb);
                  setIsEditDialogOpen(true);
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}

        <LoadingOverlay isLoading={loading} />

        {knowledgeBases.length === 0 && !loading && (
          <div className="col-span-full flex flex-col items-center justify-center p-12 bg-muted/30 rounded-lg">
            <Book className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              Nenhuma base de conhecimento encontrada
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setIsCreateDialogOpen(true)}
            >
              Criar Base
            </Button>
          </div>
        )}
      </div>

      <PaginationControl
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={limit}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <Drawer open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DrawerContent>
          <KnowledgeBaseForm
            type="create"
            knowledgeBase={currentKnowledgeBase}
            onClose={() => setIsCreateDialogOpen(false)}
            onSave={handleCreateKnowledgeBase}
          />
        </DrawerContent>
      </Drawer>

      <Drawer open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DrawerContent>
          <KnowledgeBaseForm
            type="edit"
            knowledgeBase={currentKnowledgeBase}
            onClose={() => setIsEditDialogOpen(false)}
            onSave={handleEditKnowledgeBase}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
}
